import type { ReactElement } from 'react';

import { MemberPicker } from '@/features/events/MemberPicker';
import { eventSurveyFormOptions } from '@/features/events/eventSurveyTabs';
import { PARTICIPATION_FORMAT_LABELS, type ParticipationFormat } from '@/features/events/api';
import type { EventType } from '@/features/masters/api';
import type { Staff } from '@/features/staff/api';
import { staffLabel } from '@/features/staff/api';

export interface EventFormValues {
  type_id: string;
  title: string;
  description: string;
  starts_at: string;
  ends_at: string;
  venue: string;
  meet_url: string;
  capacity: string;
  /** true=人数を指定 / false=設定しない（API は capacity=null） */
  capacity_limited: boolean;
  social_capacity: string;
  /** 出欠を取るか・交流会があるかは、種別ではなくイベントごとに決める（D-117・D-127） */
  attendance_required: boolean;
  /** 出欠回答期限（datetime-local）。出欠を取るとき必須（D-285） */
  attendance_deadline_at: string;
  has_social: boolean;
  /** オフライン / オンライン / 両方（D-223） */
  participation_format: ParticipationFormat;
  /** Meet参加ログを取るか。未選択は null（作成時は必須 / D-225） */
  meet_log_enabled: boolean | null;
  pre_survey_enabled: boolean;
  pre_assignment_enabled: boolean;
  post_survey_enabled: boolean;
  post_assignment_enabled: boolean;
  day_survey_enabled: boolean;
  /** 理事・講師向け（月例会 / D-349） */
  director_survey_enabled: boolean;
  /** 1対1の面談のときだけ使う（D-118） */
  member_id: string;
  counterpart_user_id: string;
}

export const EMPTY_EVENT_FORM: EventFormValues = {
  type_id: '',
  title: '',
  description: '',
  starts_at: '',
  ends_at: '',
  venue: '',
  meet_url: '',
  capacity: '',
  capacity_limited: false,
  social_capacity: '',
  attendance_required: true,
  attendance_deadline_at: '',
  has_social: false,
  participation_format: 'both',
  meet_log_enabled: null,
  pre_survey_enabled: true,
  pre_assignment_enabled: true,
  post_survey_enabled: true,
  post_assignment_enabled: true,
  day_survey_enabled: true,
  director_survey_enabled: true,
  member_id: '',
  counterpart_user_id: '',
};

/** フォームの定員を API 用に変換（未設定は null） */
export function capacityForApi(values: EventFormValues): number | null {
  if (!values.capacity_limited || !values.capacity.trim()) return null;
  const n = Number(values.capacity);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/**
 * イベントの作成・編集で共通する入力欄。
 * 種別を選ぶと入力項目が切り替わる（D-118）。
 * - サクセス面談・1on1（1対1の面談）: 会場や定員の代わりに、対象の受講生と相手を選ぶ
 *
 * 対象の受講生と相手は**新規登録のときだけ**入力する（`withCounterpart`）。
 * 作成後の変更は対象者・担当者の作り直しになるため、編集画面では出さない。
 */
export function EventFormFields({
  values,
  onChange,
  eventTypes,
  staff = [],
  withCounterpart = false,
  fixedType = false,
  /** サクセス面談を事務局が編集・作成するとき（Meet URL・日程は担当サクセス / D-365） */
  hideMeetUrlAndSchedule = false,
  /** 担当サクセスがMeet URLだけ編集するとき */
  meetUrlOnly = false,
}: {
  values: EventFormValues;
  onChange: (values: EventFormValues) => void;
  eventTypes: EventType[];
  staff?: Staff[];
  /** 対象の受講生・相手の入力を出すか（新規登録のみ true） */
  withCounterpart?: boolean;
  /** 種別ごとの一覧から来て種別が決まっているとき true（プルダウンを出さない / D-184） */
  fixedType?: boolean;
  hideMeetUrlAndSchedule?: boolean;
  meetUrlOnly?: boolean;
}): ReactElement {
  function set<K extends keyof EventFormValues>(key: K, value: EventFormValues[K]): void {
    onChange({ ...values, [key]: value });
  }

  /** 種別を変えたら、面談用の入力を選び直してもらう */
  function setType(typeId: string): void {
    const next = eventTypes.find((t) => t.id === typeId);
    const nextRole = next?.counterpart_role ?? null;
    const isMonthly = next?.code === 'monthly';
    onChange({
      ...values,
      type_id: typeId,
      // 1対1の面談では交流会も出欠も使わない。1on1だけ事後アンケートを使う（D-271）
      ...(nextRole
        ? {
            has_social: false,
            attendance_required: true,
            attendance_deadline_at: '',
            pre_survey_enabled: false,
            pre_assignment_enabled: false,
            post_survey_enabled: nextRole === 'director',
            post_assignment_enabled: false,
            day_survey_enabled: false,
            director_survey_enabled: false,
          }
        : isMonthly
          ? {
              attendance_required: false,
              attendance_deadline_at: '',
              capacity_limited: false,
              capacity: '',
            }
          : {}),
      // 種別を変えたら、面談用の入力は選び直してもらう
      member_id: '',
      counterpart_user_id: '',
    });
  }

  const selectedType = eventTypes.find((t) => t.id === values.type_id);
  // 相手のロールが決まっている種別が「1対1の面談」（サクセス面談・1on1 / D-123）
  const counterpartRole = selectedType?.counterpart_role ?? null;
  const isOneOnOne = counterpartRole !== null;
  const isMonthly = selectedType?.code === 'monthly';
  const counterpartLabel = counterpartRole === 'success' ? 'サクセス担当' : '理事・講師';
  const counterpartCandidates = staff.filter((s) => s.role === counterpartRole);

  if (meetUrlOnly) {
    return (
      <div>
        <Field label="Meet URL" span2>
          <input
            value={values.meet_url}
            onChange={(e) => set('meet_url', e.target.value)}
            className="w-full rounded-sm border border-wpborder px-3 py-2"
            placeholder="https://meet.google.com/..."
          />
        </Field>
        <p className="text-wptext-muted">
          各自の @cxo-growth アカウントで発行したMeet URLを登録してください。登録後「Meetリンクを案内する」で受講生へ送れます。
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <Field label="種別" required span2>
        {fixedType ? (
          // 種別ごとの一覧から来たときは決まっている。別の種別を作るときは
          // その種別のメニューから入り直してもらう（選び間違いを防ぐ / D-184）
          <p className="py-1.5 font-semibold text-wptext-strong">
            {eventTypes.find((t) => t.id === values.type_id)?.name ?? '…'}
          </p>
        ) : (
          <select
            value={values.type_id}
            onChange={(e) => setType(e.target.value)}
            className="w-full rounded-sm border border-wpborder px-3 py-2"
          >
            <option value="">選択してください</option>
            {eventTypes.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
                {!t.is_active ? '（無効）' : ''}
              </option>
            ))}
          </select>
        )}
      </Field>

      {isOneOnOne && withCounterpart && (
        <p className="text-wptext-muted sm:col-span-2">
          1対1の面談です。対象の受講生と{counterpartLabel}を選ぶと、その2人だけが参加する予定として
          公開し、<strong>この回の連絡用グループ（メッセージ）</strong>も作ります。参加者が2人でも、
          扱いは他のイベントと同じです。出欠や席次は使いません。
          <br />
          <strong>日時が決まっていなくても登録できます。</strong>
          先に相手を割り当ててグループを作り、その中で日程を決めてから、
          イベント詳細の「編集」で日時を入れてください。
        </p>
      )}
      {isOneOnOne && !withCounterpart && !hideMeetUrlAndSchedule && (
        <p className="text-wptext-muted sm:col-span-2">
          1対1の面談です。<strong>対象の受講生と相手は登録時に決まっており、ここでは変えられません</strong>
          （変える場合は登録し直してください）。日程が決まったら、下の開始日時を入れて保存すると
          この回のグループにお知らせが入ります。
        </p>
      )}
      {isOneOnOne && hideMeetUrlAndSchedule && (
        <p className="text-wptext-muted sm:col-span-2">
          サクセス面談のMeet URLと日程は<strong>担当サクセス</strong>が扱います。事務局はタイトルや説明などだけ編集できます。
          日程は担当サクセスが「実施可能日」を登録し、受講生が選んで確定します。
        </p>
      )}

      <Field label="タイトル" required span2>
        <input
          value={values.title}
          onChange={(e) => set('title', e.target.value)}
          className="w-full rounded-sm border border-wpborder px-3 py-2"
        />
      </Field>

      {isOneOnOne && withCounterpart && (
        <>
          {/* 受講生は人数が多いので、期・コースで絞ってからラジオで選ぶ（D-176） */}
          <Field label="対象の受講生" required span2>
            <MemberPicker value={values.member_id} onChange={(id) => set('member_id', id)} />
          </Field>
          <Field label={`相手（${counterpartLabel}）`} required>
            <select
              value={values.counterpart_user_id}
              onChange={(e) => set('counterpart_user_id', e.target.value)}
              className="w-full rounded-sm border border-wpborder px-3 py-2"
            >
              <option value="">選択してください</option>
              {counterpartCandidates.map((s) => (
                <option key={s.user_id} value={s.user_id}>
                  {staffLabel(s)}
                </option>
              ))}
            </select>
          </Field>
        </>
      )}

      {/* 面談は日時未定で作れる（D-173）。サクセス面談の日程は担当サクセスが扱う（D-365） */}
      {!hideMeetUrlAndSchedule && (
        <>
          <Field label={isOneOnOne ? '開始日時（決まっていれば）' : '開始日時'} required={!isOneOnOne}>
            <input
              type="datetime-local"
              value={values.starts_at}
              onChange={(e) => set('starts_at', e.target.value)}
              className="w-full rounded-sm border border-wpborder px-3 py-2"
            />
          </Field>
          <Field label="終了日時">
            <input
              type="datetime-local"
              value={values.ends_at}
              onChange={(e) => set('ends_at', e.target.value)}
              className="w-full rounded-sm border border-wpborder px-3 py-2"
            />
          </Field>
        </>
      )}

      {!isOneOnOne && (
        <div className="sm:col-span-2">
          <span className="mb-1.5 block text-wptext-muted">
            参加形式 <span className="text-wpnotice-error">*</span>
          </span>
          {(
            [
              ['offline', PARTICIPATION_FORMAT_LABELS.offline],
              ['online', PARTICIPATION_FORMAT_LABELS.online],
              ['both', PARTICIPATION_FORMAT_LABELS.both],
            ] as const
          ).map(([value, label]) => (
            <label key={value} className="mr-4 inline-flex items-center gap-1">
              <input
                type="radio"
                name="participation_format"
                checked={values.participation_format === value}
                onChange={() => set('participation_format', value)}
              />
              {label}
            </label>
          ))}
          <p className="mt-1 text-wptext-muted">
            両方を選ぶと、受講生は現地参加かオンライン参加かを選べます。
          </p>
        </div>
      )}

      {!isOneOnOne && (
        <Field label="会場">
          <input
            value={values.venue}
            onChange={(e) => set('venue', e.target.value)}
            className="w-full rounded-sm border border-wpborder px-3 py-2"
          />
        </Field>
      )}

      {!hideMeetUrlAndSchedule && (
        <Field label="Meet URL">
          <input
            value={values.meet_url}
            onChange={(e) => set('meet_url', e.target.value)}
            className="w-full rounded-sm border border-wpborder px-3 py-2"
          />
        </Field>
      )}

      {!isOneOnOne && !isMonthly && (
        <Field label="定員（本編）">
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap gap-4">
              <label className="inline-flex items-center gap-1">
                <input
                  type="radio"
                  name="capacity_mode"
                  checked={!values.capacity_limited}
                  onChange={() =>
                    onChange({ ...values, capacity_limited: false, capacity: '' })
                  }
                />
                設定しない
              </label>
              <label className="inline-flex items-center gap-1">
                <input
                  type="radio"
                  name="capacity_mode"
                  checked={values.capacity_limited}
                  onChange={() =>
                    onChange({
                      ...values,
                      capacity_limited: true,
                      capacity:
                        values.capacity ||
                        (selectedType?.code === 'subcommittee' ? '20' : ''),
                    })
                  }
                />
                人数を指定
              </label>
            </div>
            {values.capacity_limited && (
              <input
                type="number"
                min={1}
                value={values.capacity}
                onChange={(e) => set('capacity', e.target.value)}
                className="w-full max-w-xs rounded-sm border border-wpborder px-3 py-2"
                placeholder={selectedType?.code === 'subcommittee' ? '20' : '例: 50'}
              />
            )}
            {selectedType?.code === 'subcommittee' && values.capacity_limited && (
              <p className="text-sm text-wptext-muted">
                分科会は定員を設定すると、出欠の先着順で参加が確定します（定員に達すると参加の申請はできません）。
              </p>
            )}
            {!values.capacity_limited && (
              <p className="text-sm text-wptext-muted">
                定員を設定しない場合、出欠の先着順による締め切りは行いません。
              </p>
            )}
          </div>
        </Field>
      )}

      {!isOneOnOne && isMonthly && (
        <p className="text-wptext-muted sm:col-span-2">
          月例会では出欠の入力・督促・集計は行いません（D-333）。定員も設けません（D-359）。
        </p>
      )}

      {!isOneOnOne && !isMonthly && (
        <>
          <label className="flex items-center gap-2 sm:col-span-2">
            <input
              type="checkbox"
              checked={values.attendance_required}
              onChange={(e) =>
                onChange({
                  ...values,
                  attendance_required: e.target.checked,
                  attendance_deadline_at: e.target.checked ? values.attendance_deadline_at : '',
                })
              }
            />
            <span>
              出欠を取る
              <span className="ml-2 text-wptext-muted">
                外すと、このイベントは出欠の入力・督促・集計の対象になりません。
              </span>
            </span>
          </label>
          {values.attendance_required && (
            <Field label="出欠の回答期限" required>
              <input
                type="datetime-local"
                value={values.attendance_deadline_at}
                onChange={(e) => set('attendance_deadline_at', e.target.value)}
                className="w-full rounded-sm border border-wpborder px-3 py-2"
              />
            </Field>
          )}
        </>
      )}

      {!isOneOnOne && (
        <>
          <div className="sm:col-span-2">
            <span className="mb-1.5 block text-wptext-muted">
              参加ログ <span className="text-wpnotice-error">*</span>
            </span>
            <label className="mr-4 inline-flex items-center gap-1">
              <input
                type="radio"
                name="meet_log_enabled"
                checked={values.meet_log_enabled === true}
                onChange={() => set('meet_log_enabled', true)}
              />
              取る
            </label>
            <label className="mr-4 inline-flex items-center gap-1">
              <input
                type="radio"
                name="meet_log_enabled"
                checked={values.meet_log_enabled === false}
                onChange={() => set('meet_log_enabled', false)}
              />
              取らない
            </label>
            {values.meet_log_enabled === true && (
              <p className="mt-1 text-wptext-muted">
                参加ログは、Meetを発行した<strong>主催者のGoogleアカウント</strong>
                でしかAPIから取得できません。運営で使う指定のGoogleアカウントで会議を発行してください。個人アカウントで発行した会議のログは取得できません。
              </p>
            )}
          </div>
          {eventSurveyFormOptions(selectedType?.code).map(({ field, label }) => (
            <label key={field} className="flex items-center gap-2 sm:col-span-2">
              <input
                type="checkbox"
                checked={values[field]}
                onChange={(e) => set(field, e.target.checked)}
              />
              <span>
                {label}
                <span className="ml-2 text-wptext-muted">
                  外すと、この回では使わないものとして扱います。
                </span>
              </span>
            </label>
          ))}
        </>
      )}

      {counterpartRole === 'director' && (
        <label className="flex items-center gap-2 sm:col-span-2">
          <input
            type="checkbox"
            checked={values.post_survey_enabled}
            onChange={(e) => set('post_survey_enabled', e.target.checked)}
          />
          <span>
            事後アンケートを使う
            <span className="ml-2 text-wptext-muted">
              外すと、この回では使わないものとして扱います。
            </span>
          </span>
        </label>
      )}

      <Field label="説明" span2>
        <textarea
          rows={4}
          value={values.description}
          onChange={(e) => set('description', e.target.value)}
          className="w-full rounded-sm border border-wpborder px-3 py-2"
        />
      </Field>
    </div>
  );
}

function Field({
  label,
  required,
  span2,
  children,
}: {
  label: string;
  required?: boolean;
  span2?: boolean;
  children: ReactElement;
}): ReactElement {
  return (
    <label className={`block ${span2 ? 'sm:col-span-2' : ''}`}>
      <span className="mb-1.5 block text-wptext-muted">
        {label}
        {required && <span className="text-wpnotice-error"> *</span>}
      </span>
      {children}
    </label>
  );
}
